/**
 * Hardware-bound device fingerprint.
 *
 * Deliberately uses ONLY signals that reflect the physical PC — GPU, CPU, RAM,
 * monitor, OS architecture — and EXCLUDES software-level signals the user can
 * change on the fly (timezone, language, dark/light mode, fonts). If a
 * component of the physical PC changes (GPU swap, monitor swap, RAM upgrade,
 * OS reinstall on different hardware), the fingerprint changes and the device
 * is forced back through the admin-approval flow. That's the intended
 * behaviour.
 *
 * Not cached. Recomputed on every boot (<5 ms). Caching in localStorage would
 * let a swapped-hardware PC keep the old fingerprint — we want the opposite.
 */

async function sha256(s) {
    const buf = new TextEncoder().encode(s);
    const hash = await crypto.subtle.digest('SHA-256', buf);
    return Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Read GPU vendor + renderer via WebGL. This is the strongest hardware signal
 * available in the browser — it bakes in the GPU silicon, its driver build,
 * and (on Windows) the ANGLE backend version. Swapping the GPU flips this.
 *
 * Some privacy-focused browsers deny the UNMASKED_* parameters; we fall back
 * to the masked renderer string which is still hardware-derived though less
 * specific. If WebGL itself is blocked we record the absence as a signal.
 */
function getGpuSignature() {
    try {
        const canvas = document.createElement('canvas');
        const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
        if (!gl) return 'no-webgl';
        const dbg = gl.getExtension('WEBGL_debug_renderer_info');
        const vendor   = dbg ? gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL)   : gl.getParameter(gl.VENDOR);
        const renderer = dbg ? gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER);
        return `${vendor || 'unknown'}::${renderer || 'unknown'}`;
    } catch {
        return 'webgl-err';
    }
}

export async function getDeviceFingerprint() {
    const parts = [
        // Physical hardware — stable across OS user accounts and browser reinstalls
        navigator.platform || '',                                   // OS architecture (Win32/MacIntel/Linux x86_64)
        navigator.hardwareConcurrency || '',                        // CPU logical core count
        navigator.deviceMemory || '',                               // approx RAM in GB (rounded)
        (window.screen?.width || '') + 'x' + (window.screen?.height || ''),
        window.screen?.colorDepth || '',
        (window.screen?.pixelDepth || ''),
        (window.devicePixelRatio || '').toString(),                 // monitor DPI scaling
        getGpuSignature(),                                          // GPU — the strongest single signal
    ].join('|');

    return sha256('oralzy-hw:v2:' + parts);
}

export function getDeviceContext() {
    return {
        userAgent: navigator.userAgent,
        platform:  navigator.platform,
        gpu:       getGpuSignature(),
    };
}

/**
 * No-op kept for API compatibility with existing callers. Nothing is cached
 * any more — the fingerprint is derived from live hardware signals each
 * time `getDeviceFingerprint()` is called.
 */
export function clearDeviceFingerprint() {
    try { localStorage.removeItem('oralzy_device_fp'); } catch {}
}
